import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, PAPELES, type Contenedor } from '../api';
import { mensajeDeError } from '../lib/useApi';
import type { Pregunta } from './ui';
import { abrir } from './movimiento';
import { aviso, botonPrimario, botonSecundario, c, campo, etiquetaCampo, mono } from '../theme';

/**
 * Los datos de un contenedor para corregirlos: factura, doc. de recepción,
 * lote, número, fecha y muelle. Lo usan Documentos de contenedores y la ficha
 * del contenedor, que editan lo mismo con la misma ruta.
 */

/** Un dato que puede faltar: sin cargar se ve en ámbar, que es lo que hay que completar. */
export function Dato({ valor }: { valor: string }) {
  return valor ? (
    <span style={{ fontFamily: mono, color: c.texto }}>{valor}</span>
  ) : (
    <span style={{ color: c.ambar }}>sin cargar</span>
  );
}

/** Borrar arrastra todo lo que bajó de él: se tipea el código, un clic de más no alcanza. */
export function preguntaDeBorrado(ct: Pick<Contenedor, 'id' | 'pallets'>): Pregunta {
  const suyos = ct.pallets === 1 ? 'su pallet' : `sus ${ct.pallets} pallets`;
  return {
    titulo: `¿Borrar el contenedor ${ct.id}?`,
    detalle: ct.pallets ? `Se borran también ${suyos}. No se puede deshacer.` : 'No tiene pallets. No se puede deshacer.',
    boton: ct.pallets ? `Borrar el contenedor y ${suyos}` : 'Borrar el contenedor',
    tipear: ct.id,
  };
}

export function EdicionContenedor({
  ct,
  muelles,
  onCancelar,
  onGuardado,
}: {
  ct: Contenedor;
  muelles: string[];
  onCancelar: () => void;
  /** Con el código con el que quedó: puede haberse corregido. */
  onGuardado: (id: string) => void;
}) {
  const [form, setForm] = useState({
    codigo: ct.id,
    factura: ct.factura,
    docRecepcion: ct.docRecepcion,
    lote: ct.lote,
    fecha: ct.fechaIso,
    numero: ct.numero ? String(ct.numero) : '',
    muelle: ct.muelle ?? '',
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const cambiar = (cambio: Partial<typeof form>) => setForm((f) => ({ ...f, ...cambio }));

  async function guardar() {
    setGuardando(true);
    setError('');
    try {
      const r = await api.editarContenedor(ct.id, {
        ...form,
        muelle: form.muelle || null,
        numero: form.numero.trim() ? Number(form.numero) : null,
      });
      onGuardado(r.id);
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    // El formulario se abre desde el centro cuando la tarjeta de la ficha ya
    // cambió de alto: la caja llega primero y el contenido nace adentro.
    <motion.div
      {...abrir(0)}
      transition={{ ...abrir(0).transition, delay: 0.12 }}
      style={{
        overflow: 'hidden',
        padding: 16,
        borderBottom: `1px solid ${c.borde}`,
        background: c.verdeTinte,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 600, color: c.texto }}>{ct.id}</span>
      {/* Una columna en el celular (campos a lo ancho) y la grilla de siempre desde tablet. */}
      <div className="grid grid-cols-1 gap-3 tablet:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
        {/* Por si se tipeó mal al darlo de alta: todo lo que bajó de él lo sigue. */}
        <label style={etiquetaCampo}>
          Código del contenedor
          <input
            value={form.codigo}
            onChange={(e) => cambiar({ codigo: e.target.value.toUpperCase() })}
            autoComplete="off"
            spellCheck={false}
            style={{ ...campo, fontFamily: mono }}
          />
        </label>
        {PAPELES.map((p) => (
          <label key={p.campo} style={etiquetaCampo}>
            {p.nombre}
            <input
              value={form[p.campo]}
              onChange={(e) => cambiar({ [p.campo]: e.target.value })}
              placeholder={p.muestra}
              autoComplete="off"
              style={{ ...campo, fontFamily: mono }}
            />
          </label>
        ))}
        <label style={etiquetaCampo}>
          Nº de contenedor
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={form.numero}
            onChange={(e) => cambiar({ numero: e.target.value })}
            style={campo}
          />
          <span style={{ fontSize: 12, color: c.texto3, fontWeight: 400 }}>
            1 es el más viejo: sus pallets se sugieren primero.
          </span>
        </label>
        <label style={etiquetaCampo}>
          Fecha de arribo
          <input type="date" value={form.fecha} onChange={(e) => cambiar({ fecha: e.target.value })} style={campo} />
        </label>
        <label style={etiquetaCampo}>
          Muelle
          <select
            value={form.muelle}
            onChange={(e) => cambiar({ muelle: e.target.value })}
            style={{ ...campo, padding: '0 12px' }}
          >
            <option value="">Sin asignar</option>
            {muelles.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </div>
      <AnimatePresence>
        {error && (
          <motion.div key={error} role="alert" {...abrir(16)} style={aviso('err')}>
            {error}
          </motion.div>
        )}
      </AnimatePresence>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={guardar}
          disabled={guardando}
          style={{ ...botonPrimario, opacity: guardando ? 0.6 : 1 }}
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        <button type="button" onClick={onCancelar} style={botonSecundario}>
          Cancelar
        </button>
      </div>
    </motion.div>
  );
}
